import React from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { RxDashboard } from 'react-icons/rx';
import { HiOutlineUsers, HiOutlineBookOpen, HiOutlineCog6Tooth, HiOutlineCalendarDays, HiOutlineFolderOpen } from 'react-icons/hi2';
import { TbReportAnalytics } from 'react-icons/tb';
import { BiMessageSquareDetail } from 'react-icons/bi';
import { IoSearchOutline, IoNotificationsOutline, IoRocketOutline, IoNotifications } from 'react-icons/io5';
import { HiPlus } from 'react-icons/hi2';

const ADMIN_NAV = [
  { path: '/admin', label: '대시보드', icon: RxDashboard },
  { path: '/admin/users', label: '사용자 관리', icon: HiOutlineUsers, superOnly: true },
  { path: '/admin/classes', label: '클래스 관리', icon: HiOutlineBookOpen },
  { path: '/admin/materials', label: '자료 관리', icon: HiOutlineFolderOpen },
  { path: '/admin/schedule', label: '일정 관리', icon: HiOutlineCalendarDays },
  { path: '/admin/reports', label: '리포트/분석', icon: TbReportAnalytics },
  { path: '/admin/messages', label: '메시지 관리', icon: BiMessageSquareDetail },
  { path: '/admin/notifications', label: '알림 문구', icon: IoNotifications, superOnly: true },
  { path: '/admin/settings', label: '설정', icon: HiOutlineCog6Tooth, superOnly: true },
  { path: '/admin/updates', label: '웹 업데이트', icon: IoRocketOutline, superOnly: true },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // 관리자 로그인 체크
  const adminSession = localStorage.getItem('adminSession');
  if (!adminSession) {
    return <Navigate to="/admin/login" replace />;
  }

  const admin = JSON.parse(adminSession);

  return (
    <Container data-admin>
      <Sidebar>
        <LogoArea onClick={() => navigate('/admin')}>
          <LogoIcon>1급</LogoIcon>
          <LogoText>1등급</LogoText>
        </LogoArea>
        <RoleBadgeWrap>
          {admin.adminRole === 'superadmin' && <RoleBadgeTag $color="#4A7CFE">슈퍼관리자</RoleBadgeTag>}
          {admin.adminRole === 'teacher_all' && <RoleBadgeTag $color="#22C55E">강사 (모든 클래스)</RoleBadgeTag>}
          {(admin.adminRole === 'teacher_own' || admin.adminRole === 'teacher') && <RoleBadgeTag $color="#22C55E">강사 (자기 클래스)</RoleBadgeTag>}
          {admin.adminRole === 'assistant' && <RoleBadgeTag $color="#F59E0B">조교 (읽기전용)</RoleBadgeTag>}
        </RoleBadgeWrap>
        <Nav>
          {ADMIN_NAV
            .filter((item) => !item.superOnly || admin.adminRole === 'superadmin')
            .map((item) => {
              const isActive = item.path === '/admin'
                ? location.pathname === '/admin'
                : location.pathname.startsWith(item.path);
              return (
                <NavItem key={item.path} $active={isActive} onClick={() => navigate(item.path)}>
                  <item.icon size={18} />
                  <span>{item.label}</span>
                </NavItem>
              );
            })}
        </Nav>
        <SidebarFooter>
   
          <BackButton onClick={() => {
            localStorage.removeItem('adminSession');
            navigate('/admin/login');
          }}>
            로그아웃
          </BackButton>
        </SidebarFooter>
      </Sidebar>

      <MainArea>
        <TopBar>
          <SearchBox>
            <IoSearchOutline size={18} color="#9E9E9E" />
            <SearchInput placeholder="사용자, 클래스, 과제 검색..." />
          </SearchBox>
          <TopBarRight>
       
            <ProfileChip>
              <ProfileAvatar>{admin.name?.charAt(0) || '관'}</ProfileAvatar>
              <span>{admin.name || '관리자'}</span>
            </ProfileChip>
          </TopBarRight>
        </TopBar>

        <Content>
          <Outlet />
        </Content>
      </MainArea>
    </Container>
  );
};

export default AdminLayout;

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  max-width: none !important;
  background: #F7F8FA;
`;

const Sidebar = styled.aside`
  width: 220px;
  background: #FFFFFF;
  border-right: 1px solid #EAEDF3;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 10;
`;

const LogoArea = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  border-bottom: 1px solid #EAEDF3;
`;

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #4A7CFE;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
`;

const LogoText = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #1A1A2E;
`;

const RoleBadgeWrap = styled.div`
  padding: 8px 12px 0;
`;

const RoleBadgeTag = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => $color}15;
  color: ${({ $color }) => $color};
`;

const Nav = styled.nav`
  flex: 1;
  padding: 12px 8px;
`;

const NavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  color: ${({ $active }) => $active ? '#111827' : '#374151'};
  background: ${({ $active }) => $active ? '#EEF2FF' : 'transparent'};
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? 600 : 500};
  margin-bottom: 2px;
  transition: all 0.15s;
  &:hover { background: ${({ $active }) => $active ? '#EEF2FF' : '#F9FAFB'}; }
`;

const SidebarFooter = styled.div`
  padding: 12px;
  border-top: 1px solid #EAEDF3;
`;

const BackButton = styled.button`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  color: #9CA3AF;
  font-size: 13px;
  &:hover { background: #F9FAFB; color: #6B7280; }
`;

const MainArea = styled.div`
  flex: 1;
  margin-left: 220px;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #FFFFFF;
  border-bottom: 1px solid #EAEDF3;
  position: sticky;
  top: 0;
  z-index: 5;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #F7F8FA;
  border: 1px solid #EAEDF3;
  border-radius: 8px;
  width: 320px;
`;

const SearchInput = styled.input`
  border: none;
  background: none;
  font-size: 14px;
  color: #374151;
  width: 100%;
  &::placeholder { color: #9CA3AF; }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const QuickCreateBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #4A7CFE;
  color: white;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  &:hover { background: #3B6DE8; }
`;

const NotiBadge = styled.button`
  position: relative;
  color: #6B7280;
  padding: 6px;
  border-radius: 8px;
  &:hover { background: #F3F4F6; }
`;

const NotiDot = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #EF4444;
  border: 1.5px solid white;
`;

const ProfileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  border: 1px solid #EAEDF3;
  border-radius: 20px;
  cursor: pointer;
  span { font-size: 13px; font-weight: 500; color: #374151; }
  &:hover { background: #F9FAFB; }
`;

const ProfileAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #4A7CFE;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const Content = styled.main`
  flex: 1;
  padding: 24px;
`;
